<script setup lang="ts">
import 'jquery-validation';

import $ from 'jquery';
import type { PropType } from 'vue';
import {
    computed,
    inject,
    onMounted,
    ref,
    shallowRef,
    unref,
    watch,
} from 'vue';

import { setupAutofillControl, setupAutoprettifyControls } from '#public/commons-global';
import { useGenerateIds, useJqueryEventListener } from '#public_ts/composables';

import updateDataAttributes from './data-attributes';
import { ValidatorInstance } from './injection';

const props = defineProps({
    id: { type: String as PropType<string | undefined>, required: false, default: undefined },
    name: { type: String as PropType<string | undefined>, required: false, default: undefined },
    modelValue: { type: [String, Number, null] as PropType<string | number | null | undefined>, required: false, default: undefined },
    booleanValue: { type: [Boolean, null] as PropType<boolean | null | undefined>, required: false, default: undefined },
    type: { type: String as PropType<string | undefined>, required: false, default: undefined },
    data: { type: Object as PropType<Record<string, unknown> | undefined>, required: false, default: undefined },
    plain: { type: Boolean as PropType<boolean | undefined>, required: false, default: undefined },
});
//eslint-obsolete no-spaced-func
const emit = defineEmits<{
    (event: 'update:modelValue', value: string): void;
    (event: 'update:booleanValue', value: boolean): void;
}>();

const input = shallowRef<HTMLInputElement | undefined>();
const localModelValue = ref(props.modelValue);
const localBooleanValue = ref(props.booleanValue);
const validatorInstance = inject(ValidatorInstance, undefined);
const generateIds = useGenerateIds();
const controlId = props.id || generateIds();
const controlName = (props.name === '--') ? undefined : (props.name || controlId);
const isCheckbox = computed(() => (props.type == 'checkbox') || (props.type == 'radio'));

defineExpose({ input });

function setupValidator() {
    const _input = unref(input);
    const _validatorInstance = unref(validatorInstance);
    if(_input && _validatorInstance) {
        const rules: JQueryValidation.RulesDictionary | undefined = $(_input).rules();
        if(rules && Object.keys(rules).length) {
            // parent form does this too, but function supports reentrance
            // this is specially for dynamically created controls
            setupAutofillControl($(_input), rules, _validatorInstance);
        }
    }
}

if(validatorInstance) {
    watch(validatorInstance, setupValidator, { immediate: true });
}

watch(() => props.data, (val) => {
    const _input = unref(input);
    if(_input && val) {
        updateDataAttributes($(_input), val);
    }
}, { deep: true });

onMounted(() => {
    const _input = unref(input);
    if(_input) {
        if(props.data) {
            updateDataAttributes($(_input), props.data);
        }
        // parent form does this too, but function supports reentrance
        // this is specially for dynamically created controls
        setupAutoprettifyControls($(_input));
        setupValidator();
    }
});

function onInput(e: Event) {
    if(unref(isCheckbox)) {
        const val = $(e.target as HTMLInputElement)?.is(':checked');
        localBooleanValue.value = val;
        emit('update:booleanValue', val);
    } else {
        const val = (e.target as HTMLInputElement)?.value;
        localModelValue.value = val;
        emit('update:modelValue', val);
    }
}

//autoformat support
//because autoformat uses jquery events, we need also
useJqueryEventListener(input, 'blur autoformat auto-changed', () => {
    if(unref(isCheckbox)) {
        return;
    }
    // no nextTick here, vue will reset value to default
    const _input = unref(input);
    if(_input && !$(_input).is('[data-autoprettify-disabled="true"]')) {
        const val = $(_input).val();
        if((val !== undefined) && (typeof (val) === 'string') && (val !== props.modelValue)) {
            localModelValue.value = val;
            emit('update:modelValue', val);
        }
    }
});

watch(() => props.modelValue, (val) => {
    if(val !== unref(localModelValue)) {
        localModelValue.value = val;
    }
});

watch(() => props.booleanValue, (val) => {
    if(val !== unref(localBooleanValue)) {
        localBooleanValue.value = val;
    }
});
</script>
<template>
    <input
        v-if="props.type !== 'textarea'"
        v-bind="{
            checked: isCheckbox ? (localBooleanValue || undefined) : undefined,
            value: isCheckbox ? (localModelValue || controlId) : (localModelValue || '')
        }"
        :id="controlId"
        ref="input"
        :name="controlName"
        :type="props.type || 'text'"
        :class="/*ugly default styling*/ (isCheckbox || props.plain) ? '' : 'form-control'"
        @input="onInput"
        @change="onInput"
    >
    <textarea
        v-else
        :id="controlId"
        ref="input"
        :name="controlName"
        :class="props.plain ? '' : 'form-control'"
        :value="localModelValue || ''"
        @input="onInput"
    />
</template>
